import es from './locales/es.js';
import en from './locales/en.js';
import de from './locales/de.js';
export const IDIOMAS_SOPORTADOS = ['es', 'en', 'de'];
export const IDIOMA_POR_DEFECTO = 'es';
export const catalogos = { es, en, de };
const catalogoBase = es;
/**
 * Traduce una clave. Si falta en el idioma pedido, cae al espanol y, si tampoco
 * esta, devuelve la propia clave: nunca se rompe la pantalla por una traduccion.
 * El test `catalogos.test.ts` impide que ese repliegue llegue a produccion.
 */
export function t(idioma, clave, params) {
    const catalogo = catalogos[idioma] ?? catalogoBase;
    const plantilla = catalogo[clave] ?? catalogoBase[clave] ?? clave;
    if (!params)
        return plantilla;
    return Object.entries(params).reduce((texto, [nombre, valor]) => texto.replaceAll(`{${nombre}}`, String(valor)), plantilla);
}
export function esIdiomaSoportado(valor) {
    return IDIOMAS_SOPORTADOS.includes(valor);
}
// ---------------------------------------------------------------------------
// Formato de numeros, importes y fechas
//
// Se delega SIEMPRE en `Intl`. No hay ninguna sustitucion manual de puntos y
// comas en este archivo, y no debe haberla en ningun otro.
//
// El motivo es que cada mercado escribe los numeros como los escribe, y un
// propietario aleman o britanico que lee "1.234,56" donde espera "1,234.56"
// percibe un error, no una convencion. La unica excepcion deliberada es
// `useGrouping: 'always'`, que fuerza el separador de millares tambien en
// cifras de cuatro digitos: el espanol lo omite por defecto, y en una
// herramienta financiera un importe de cuatro cifras sin separador se lee mal.
// ---------------------------------------------------------------------------
/** Locale completo que corresponde a cada idioma soportado. */
export const LOCALES = {
    es: 'es-ES',
    en: 'en-GB',
    de: 'de-DE',
};
export function localeDe(idioma) {
    return LOCALES[idioma] ?? LOCALES[IDIOMA_POR_DEFECTO];
}
/**
 * Formatea un importe expresado en la UNIDAD MENOR de la moneda (centimos).
 *
 * Se recibe en centimos porque asi es como el sistema guarda y transporta el
 * dinero: enteros, nunca coma flotante. La division por cien ocurre aqui, en el
 * borde de presentacion, y en ningun otro sitio.
 *
 *   formatearImporteDesdeCents(123456, 'es')  ->  "1.234,56 €"
 *   formatearImporteDesdeCents(123456, 'de')  ->  "1.234,56 €"
 *   formatearImporteDesdeCents(123456, 'en')  ->  "€1,234.56"
 */
export function formatearImporteDesdeCents(cents, idioma = IDIOMA_POR_DEFECTO, opciones = {}) {
    const { moneda = 'EUR', decimales } = opciones;
    const formateador = new Intl.NumberFormat(localeDe(idioma), {
        style: 'currency',
        currency: moneda,
        useGrouping: 'always',
        ...(decimales === undefined
            ? {}
            : { minimumFractionDigits: decimales, maximumFractionDigits: decimales }),
    });
    return formateador.format(cents / 100);
}
/** Numeros que no son dinero: cantidades, contadores, medidas. */
export function formatearNumero(valor, idioma = IDIOMA_POR_DEFECTO, opciones = {}) {
    return new Intl.NumberFormat(localeDe(idioma), { useGrouping: 'always', ...opciones }).format(valor);
}
/**
 * Porcentajes desde PUNTOS BASICOS, que es como los guarda el sistema.
 * 2.100 puntos basicos son el 21 %.
 */
export function formatearPorcentajeDesdePuntosBasicos(puntosBasicos, idioma = IDIOMA_POR_DEFECTO, decimales = 2) {
    return new Intl.NumberFormat(localeDe(idioma), {
        style: 'percent',
        useGrouping: 'always',
        minimumFractionDigits: decimales,
        maximumFractionDigits: decimales,
    }).format(puntosBasicos / 10_000);
}
/*
  UNA SOLA FORMA DE ESCRIBIR UNA FECHA EN TODA LA APLICACION: 11-09-2026.

  Convivian tres: el `2026-09-03` de las tablas, el «3 sept 2026» de las fichas
  y lo que cada idioma decidiera —en ingles, `09/03/2026`, que es el tres de
  septiembre o el nueve de marzo segun quien lo lea—. Con datos que se miran
  deprisa y personas de tres paises, eso no es un detalle de estilo.

  Se elige dia-mes-año con guiones, IGUAL EN LOS TRES IDIOMAS. No es lo que
  haria `Intl` para un ingles, y es a proposito: la ventaja de no tener que
  pensar cual es el mes vale mas que la costumbre local.
*/
const DIA_MES_ANIO = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
};
/** Pasa `11/09/2026` a `11-09-2026`, que es como se escribe aqui. */
function conGuiones(texto) {
    return texto.replace(/\//g, '-');
}
export function formatearFecha(fecha, _idioma = IDIOMA_POR_DEFECTO, opciones = DIA_MES_ANIO, zonaHoraria = 'Europe/Madrid') {
    // `en-GB` da dia/mes/año; el idioma no entra porque el formato es el mismo
    // para todos. Se queda en la firma para no tocar las decenas de llamadas.
    return conGuiones(new Intl.DateTimeFormat('en-GB', { ...opciones, timeZone: zonaHoraria }).format(fecha));
}
export function formatearFechaHora(fecha, _idioma = IDIOMA_POR_DEFECTO, zonaHoraria = 'Europe/Madrid') {
    const dia = formatearFecha(fecha, _idioma, DIA_MES_ANIO, zonaHoraria);
    const hora = new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZone: zonaHoraria,
    }).format(fecha);
    return `${dia} ${hora}`;
}
/**
 * Una fecha de calendario (`2026-09-11`) escrita como se lee aqui.
 *
 * Se fija UTC porque estas fechas son DIAS, no instantes: sin fijarla, un
 * navegador al oeste de Greenwich ensena la vispera.
 */
export function formatearDia(iso) {
    if (!iso)
        return '—';
    const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(d.getTime()))
        return iso;
    return formatearFecha(d, IDIOMA_POR_DEFECTO, DIA_MES_ANIO, 'UTC');
}
//# sourceMappingURL=index.js.map